/**
 * WhatsApp Button Component
 * Handles WhatsApp button functionality, analytics, and interactions
 */

class WhatsAppButton {
  constructor() {
    this.button = document.querySelector('.whatsapp-button');
    this.isVisible = false;
    this.scrollThreshold = 100; // Show button after scrolling 100px
    this.lastScrollY = 0;
    this.ticking = false;
    
    if (this.button) {
      this.init();
    }
  }

  init() {
    this.setupEventListeners();
    this.setupScrollBehavior();
    this.setupAccessibility();
    this.setupAnalytics();
    this.handleInitialVisibility();
  }

  setupEventListeners() {
    // Click event with analytics tracking
    this.button.addEventListener('click', (e) => {
      this.handleClick(e);
    });

    // Keyboard accessibility
    this.button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleClick(e);
      }
    });

    // Mouse events for enhanced interactions
    this.button.addEventListener('mouseenter', () => {
      this.handleMouseEnter();
    });

    this.button.addEventListener('mouseleave', () => {
      this.handleMouseLeave();
    });

    // Touch events for mobile
    this.button.addEventListener('touchstart', () => {
      this.handleTouchStart();
    }, { passive: true });

    // Window events
    window.addEventListener('scroll', () => {
      this.requestScrollUpdate();
    }, { passive: true });

    window.addEventListener('resize', () => {
      this.handleResize();
    }, { passive: true });
  }

  setupScrollBehavior() {
    // Hide button initially if scroll behavior is enabled
    if (this.shouldUseScrollBehavior()) {
      this.button.style.opacity = '0';
      this.button.style.transform = 'translateY(20px)';
      this.button.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    }
  }

  setupAccessibility() {
    // Ensure proper ARIA attributes
    if (!this.button.getAttribute('aria-label')) {
      this.button.setAttribute('aria-label', 'Open WhatsApp chat');
    }

    // Add role if not present
    if (!this.button.getAttribute('role')) {
      this.button.setAttribute('role', 'button');
    }

    // Handle focus management
    this.button.addEventListener('focus', () => {
      this.button.classList.add('whatsapp-button--focused');
    });

    this.button.addEventListener('blur', () => {
      this.button.classList.remove('whatsapp-button--focused');
    });
  }

  setupAnalytics() {
    // Store analytics data
    this.analyticsData = {
      buttonClicks: 0,
      sessionStart: Date.now(),
      lastInteraction: null
    };
  }

  handleInitialVisibility() {
    // Show button immediately if no scroll behavior
    if (!this.shouldUseScrollBehavior()) {
      this.showButton();
    }
  }

  handleClick(e) {
    // Track click for analytics
    this.trackClick();
    
    // Add click animation
    this.addClickAnimation();
    
    // Validate WhatsApp link
    const href = this.button.getAttribute('href');
    if (!this.isValidWhatsAppLink(href)) {
      e.preventDefault();
      console.warn('Invalid WhatsApp link:', href);
      this.showError('Invalid WhatsApp link');
      return;
    }

    // Track successful click
    this.trackEvent('whatsapp_button_click', {
      url: href,
      timestamp: Date.now(),
      scroll_position: window.scrollY
    });
  }

  handleMouseEnter() {
    this.button.classList.add('whatsapp-button--hover');
    
    // Pause pulse animation on hover
    if (this.button.classList.contains('whatsapp-button--pulse')) {
      this.button.style.animationPlayState = 'paused';
    }
  }

  handleMouseLeave() {
    this.button.classList.remove('whatsapp-button--hover');
    
    // Resume pulse animation
    if (this.button.classList.contains('whatsapp-button--pulse')) {
      this.button.style.animationPlayState = 'running';
    }
  }

  handleTouchStart() {
    // Add touch feedback
    this.button.classList.add('whatsapp-button--touched');
    
    setTimeout(() => {
      this.button.classList.remove('whatsapp-button--touched');
    }, 150);
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updatePosition();
    }, 250);
  }

  requestScrollUpdate() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateScrollBehavior();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateScrollBehavior() {
    if (!this.shouldUseScrollBehavior()) return;

    const currentScrollY = window.scrollY;
    const scrollingDown = currentScrollY > this.lastScrollY;
    const scrolledEnough = currentScrollY > this.scrollThreshold;

    if (scrolledEnough && !this.isVisible) {
      this.showButton();
    } else if (!scrolledEnough && this.isVisible) {
      this.hideButton();
    }

    // Auto-hide on scroll down (optional behavior)
    if (this.shouldAutoHideOnScroll()) {
      if (scrollingDown && currentScrollY > this.scrollThreshold * 2) {
        this.button.style.opacity = '0.7';
      } else {
        this.button.style.opacity = '1';
      }
    }

    this.lastScrollY = currentScrollY;
  }

  showButton() {
    if (this.isVisible) return;
    
    this.isVisible = true;
    this.button.style.opacity = '1';
    this.button.style.transform = 'translateY(0)';
    this.button.classList.add('whatsapp-button--visible');
    
    // Track visibility
    this.trackEvent('whatsapp_button_shown', {
      scroll_position: window.scrollY,
      timestamp: Date.now()
    });
  }

  hideButton() {
    if (!this.isVisible) return;
    
    this.isVisible = false;
    this.button.style.opacity = '0';
    this.button.style.transform = 'translateY(20px)';
    this.button.classList.remove('whatsapp-button--visible');
  }

  addClickAnimation() {
    this.button.classList.add('whatsapp-button--clicking');
    
    setTimeout(() => {
      this.button.classList.remove('whatsapp-button--clicking');
    }, 200);
  }

  updatePosition() {
    // Update position based on viewport changes
    // This can be extended for dynamic positioning
  }

  isValidWhatsAppLink(url) {
    if (!url) return false;
    
    const whatsappPatterns = [
      /^https?:\/\/wa\.me\//,
      /^https?:\/\/api\.whatsapp\.com\//,
      /^whatsapp:\/\//
    ];
    
    return whatsappPatterns.some(pattern => pattern.test(url));
  }

  shouldUseScrollBehavior() {
    // Check if scroll behavior should be enabled
    // Can be configured via data attributes or settings
    return this.button.dataset.scrollBehavior === 'true';
  }

  shouldAutoHideOnScroll() {
    return this.button.dataset.autoHide === 'true';
  }

  trackClick() {
    this.analyticsData.buttonClicks++;
    this.analyticsData.lastInteraction = Date.now();
  }

  trackEvent(eventName, data = {}) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'WhatsApp Button',
        event_label: 'User Interaction',
        custom_parameter_1: data.scroll_position || 0,
        custom_parameter_2: data.timestamp || Date.now(),
        ...data
      });
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', eventName, data);
    }

    // Custom analytics
    if (window.customAnalytics && typeof window.customAnalytics.track === 'function') {
      window.customAnalytics.track(eventName, data);
    }

    // Console log for debugging
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      console.log('WhatsApp Button Event:', eventName, data);
    }
  }

  showError(message) {
    // Create temporary error message
    const errorEl = document.createElement('div');
    errorEl.className = 'whatsapp-button-error';
    errorEl.textContent = message;
    errorEl.style.cssText = `
      position: fixed;
      bottom: 80px;
      right: 20px;
      background: #ff4444;
      color: white;
      padding: 10px 15px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 10001;
      animation: fadeInOut 3s ease;
    `;
    
    document.body.appendChild(errorEl);
    
    setTimeout(() => {
      if (errorEl.parentNode) {
        errorEl.parentNode.removeChild(errorEl);
      }
    }, 3000);
  }

  // Public API methods
  show() {
    this.showButton();
  }

  hide() {
    this.hideButton();
  }

  toggle() {
    if (this.isVisible) {
      this.hideButton();
    } else {
      this.showButton();
    }
  }

  getAnalytics() {
    return {
      ...this.analyticsData,
      sessionDuration: Date.now() - this.analyticsData.sessionStart,
      isVisible: this.isVisible
    };
  }

  updateLink(newUrl) {
    if (this.isValidWhatsAppLink(newUrl)) {
      this.button.setAttribute('href', newUrl);
      this.trackEvent('whatsapp_link_updated', { new_url: newUrl });
    } else {
      console.warn('Invalid WhatsApp URL provided:', newUrl);
    }
  }

  destroy() {
    // Clean up event listeners and remove button
    if (this.button) {
      this.button.remove();
    }
    
    window.removeEventListener('scroll', this.requestScrollUpdate);
    window.removeEventListener('resize', this.handleResize);
    
    clearTimeout(this.resizeTimeout);
  }
}

// CSS for animations and error states
const whatsappButtonStyles = `
  .whatsapp-button--clicking {
    transform: scale(0.95) !important;
    transition: transform 0.1s ease !important;
  }

  .whatsapp-button--focused {
    box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.3) !important;
  }

  .whatsapp-button--touched {
    transform: scale(0.98) !important;
  }

  @keyframes fadeInOut {
    0%, 100% { opacity: 0; transform: translateY(10px); }
    10%, 90% { opacity: 1; transform: translateY(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .whatsapp-button--clicking,
    .whatsapp-button--touched {
      transform: none !important;
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = whatsappButtonStyles;
document.head.appendChild(styleSheet);

// Initialize when DOM is ready
let whatsappButtonInstance = null;

function initWhatsAppButton() {
  if (document.querySelector('.whatsapp-button')) {
    whatsappButtonInstance = new WhatsAppButton();
    
    // Expose to global scope for theme customization
    window.WhatsAppButton = whatsappButtonInstance;
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhatsAppButton);
} else {
  initWhatsAppButton();
}

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId.includes('whatsapp-button')) {
      setTimeout(initWhatsAppButton, 100);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.detail.sectionId.includes('whatsapp-button') && whatsappButtonInstance) {
      whatsappButtonInstance.destroy();
      whatsappButtonInstance = null;
    }
  });
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = WhatsAppButton;
}
